import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeftIcon,
  FileTextIcon,
  CalendarIcon,
  Banknote as BanknoteIcon,
  FolderIcon,
  UserIcon,
  BuildingIcon,
  EyeIcon,
  DownloadIcon,
  FileIcon,
  ImageIcon,
  PaperclipIcon
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ClosingSummary = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [advanceRequest, setAdvanceRequest] = useState(location.state?.advanceRequest || null);
  const [expenseLines, setExpenseLines] = useState(location.state?.expenseLines || []);
  const [loading, setLoading] = useState(!advanceRequest);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [convertedAmounts, setConvertedAmounts] = useState({}); // Store converted amounts
  const [projects, setProjects] = useState([]); // Cost center projects
  const [expenseTypes, setExpenseTypes] = useState([]); // Cost center expense types

  useEffect(() => {
    // Always fetch cost centers data, even if we have advanceRequest/expenseLines from location.state
    const fetchCostCenters = async () => {
      try {
        const [projectsResponse, expenseTypesResponse] = await Promise.all([
          axios.get(`${API}/api/cost-centers/projects`),
          axios.get(`${API}/api/cost-centers/expense-types`)
        ]);
        setProjects(projectsResponse.data);
        setExpenseTypes(expenseTypesResponse.data);
      } catch (error) {
        console.error('Error fetching cost centers:', error);
      }
    };
    
    fetchCostCenters();
    
    // Fetch advance request data only if not provided in location.state
    if (!advanceRequest || !expenseLines.length) {
      fetchData();
    }
  }, [requestId]);
  
  // Convert expenses when they are loaded from location.state
  useEffect(() => {
    if (advanceRequest && expenseLines.length > 0 && Object.keys(convertedAmounts).length === 0) {
      const advanceCurrency = advanceRequest.currency;
      const conversions = {};
      
      const convertAll = async () => {
        for (const line of expenseLines) {
          if (line.currency !== advanceCurrency) {
            try {
              const response = await axios.post(`${API}/api/currency/convert`, {
                amount: parseFloat(line.amount),
                from_currency: line.currency,
                to_currency: advanceCurrency
              });
              conversions[line.id] = response.data.converted_amount;
            } catch (error) {
              console.error(`Conversion error:`, error);
              conversions[line.id] = parseFloat(line.amount);
            }
          } else {
            conversions[line.id] = parseFloat(line.amount);
          }
        }
        setConvertedAmounts(conversions);
      };
      
      convertAll();
    }
  }, [advanceRequest, expenseLines]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch advance request from paid list and cost center data in parallel
      const [paidResponse, projectsResponse, expenseTypesResponse] = await Promise.all([
        axios.get(`${API}/api/advance-requests/paid`),
        axios.get(`${API}/api/cost-centers/projects`),
        axios.get(`${API}/api/cost-centers/expense-types`)
      ]);
      
      const advance = paidResponse.data.find(r => r.id === requestId);
      
      if (!advance) {
        throw new Error('Avans bulunamadı');
      }
      
      setAdvanceRequest(advance);
      setProjects(projectsResponse.data);
      setExpenseTypes(expenseTypesResponse.data);
      
      // Fetch expense lines
      const expenseResponse = await axios.get(`${API}/api/advance-expenses/${requestId}`);
      const expenses = expenseResponse.data || [];
      setExpenseLines(expenses);
      
      // Convert all expenses to advance currency
      const advanceCurrency = advance.currency;
      const conversions = {};
      
      for (const line of expenses) {
        if (line.currency !== advanceCurrency) {
          try {
            const conversionResponse = await axios.post(`${API}/api/currency/convert`, {
              amount: parseFloat(line.amount),
              from_currency: line.currency,
              to_currency: advanceCurrency
            });
            conversions[line.id] = conversionResponse.data.converted_amount;
          } catch (error) {
            console.error(`Conversion error for line ${line.id}:`, error);
            // If conversion fails, use original amount as fallback
            conversions[line.id] = parseFloat(line.amount);
          }
        } else {
          // Same currency, no conversion needed
          conversions[line.id] = parseFloat(line.amount);
        }
      }
      
      setConvertedAmounts(conversions);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'TRY') => {
    const formatter = new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${formatter.format(amount)} ${currency}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Tarih belirtilmemiş';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Get cost center name
  const getCostCenterName = (expense) => {
    if (!expense.cost_center_type || !expense.cost_center_id) {
      return null;
    }
    
    console.log('🔍 Finding cost center:', {
      type: expense.cost_center_type,
      id: expense.cost_center_id,
      projects: projects,
      expenseTypes: expenseTypes
    });
    
    if (expense.cost_center_type === 'project') {
      const project = projects.find(p => p.id === expense.cost_center_id);
      console.log('📂 Project search result:', project);
      return project ? project.project_name : 'Proje bulunamadı';
    } else if (expense.cost_center_type === 'general_expense') {
      const expenseType = expenseTypes.find(t => t.id === expense.cost_center_id);
      console.log('📋 Expense type search result:', expenseType);
      return expenseType ? expenseType.expense_type_name : 'Gider türü bulunamadı';
    }
    
    return null;
  };

  const getTotalExpenses = () => {
    // Wait for all conversions to complete before calculating
    // Check if all expenses have been converted
    const allConverted = expenseLines.every(line => {
      // If same currency as advance, no conversion needed - already "converted"
      if (line.currency === advanceRequest?.currency) return true;
      // Otherwise check if conversion exists
      return convertedAmounts[line.id] !== undefined;
    });
    
    // If not all converted yet, return 0 to avoid showing wrong amount
    if (!allConverted) return 0;
    
    // Use converted amounts (in advance currency)
    return expenseLines.reduce((total, line) => {
      const convertedAmount = convertedAmounts[line.id] || parseFloat(line.amount) || 0;
      return total + convertedAmount;
    }, 0);
  };

  const getBalance = () => {
    const totalExpenses = getTotalExpenses();
    const approvedAmount = parseFloat(advanceRequest?.approved_amount || 0);
    return approvedAmount - totalExpenses; // Positive = refund to company, Negative = owes from company
  };

  const handleDocumentPreview = (line) => {
    if (line.attachedFile && line.attachedFile.url) {
      setPreviewDocument(line);
      setShowPreviewModal(true);
    } else {
      toast.error('Belge URL\'si bulunamadı');
    }
  };

  const getDocumentIcon = (fileName) => {
    if (!fileName) return PaperclipIcon;
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
      return ImageIcon;
    } else if (['pdf'].includes(extension)) {
      return FileTextIcon;
    } else {
      return FileIcon;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Kapama özeti yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!advanceRequest) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Avans bilgileri bulunamadı</p>
        <Button 
          onClick={() => navigate('/dashboard/closing')} 
          variant="outline" 
          className="mt-4"
        >
          Geri Dön
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate(`/dashboard/closing/${requestId}`, {
              state: {
                advanceRequest,
                expenseLines
              }
            })}
            variant="outline"
            size="sm"
            className="flex items-center"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Geri Dön
          </Button>
          
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Kapama Özeti
            </h1>
            <p className="text-gray-600">
              {advanceRequest.advance_number || `AVS-${requestId.slice(0, 8)}`}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="text-right">
            <div className="text-lg font-semibold text-purple-600">
              {formatCurrency(getTotalExpenses())}
            </div>
            <div className="text-sm text-gray-500">Toplam Harcama</div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid ${getBalance() !== 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-6`}>
        {/* 1. Paid Advance (Approved Amount) */}
        <Card className="p-6 bg-green-50">
          <div className="text-sm font-medium text-gray-600 mb-2 text-center">ÖDENEN AVANS</div>
          <div className="text-3xl font-bold text-green-600 text-center">
            {formatCurrency(advanceRequest?.approved_amount || 0, advanceRequest?.currency || 'TRY')}
          </div>
        </Card>

        {/* 2. Total Expenses */}
        <Card className="p-6">
          <div className="text-sm font-medium text-gray-600 mb-2 text-center">TOPLAM HARCANAN</div>
          <div className="text-3xl font-bold text-blue-600 text-center">
            {formatCurrency(getTotalExpenses(), advanceRequest?.currency || 'TRY')}
          </div>
        </Card>

        {/* 3. Expense Difference */}
        <Card className={`p-6 ${
          getBalance() > 0 ? 'bg-orange-50' : getBalance() < 0 ? 'bg-red-50' : 'bg-green-50'
        }`}>
          <div className="text-sm font-medium text-gray-600 mb-2 text-center">HARCAMA FARKI</div>
          <div className={`text-3xl font-bold text-center ${
            getBalance() > 0 ? 'text-orange-600' : getBalance() < 0 ? 'text-red-600' : 'text-green-600'
          }`}>
            {formatCurrency(Math.abs(getBalance()), advanceRequest?.currency || 'TRY')}
          </div>
          <div className="text-xs text-gray-500 mt-1 text-center">
            {getBalance() > 0 ? '(İade Edilecek)' : getBalance() < 0 ? '(Eksik Kalan)' : '(Dengede)'}
          </div>
        </Card>

        {/* 4. Status Box - Only show if there's a difference */}
        {getBalance() !== 0 && (
          <Card className={`p-6 border-2 ${
            getBalance() < 0 
              ? 'bg-green-600 border-green-700' 
              : 'bg-red-600 border-red-700'
          }`}>
            <div className="text-2xl font-bold text-white mb-2 text-center">
              {formatCurrency(Math.abs(getBalance()), advanceRequest?.currency || 'TRY')}
            </div>
            <div className="text-sm font-medium text-white leading-relaxed text-center">
              {getBalance() < 0 
                ? 'fazla harcamanız var ve şirketten alacaklısınız' 
                : 'az harcamanız var ve şirkete borçlusunuz'
              }
            </div>
          </Card>
        )}
      </div>

      {/* Advance Details */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Avans Detayları</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-3">
            <div className="flex items-center">
              <UserIcon className="w-4 h-4 mr-3 text-gray-400" />
              <span className="font-medium">Talep Eden:</span>
              <span className="ml-2">{advanceRequest.requester_name || 'Bilinmiyor'}</span>
            </div>
            
            <div className="flex items-center">
              <FolderIcon className="w-4 h-4 mr-3 text-gray-400" />
              <span className="font-medium">Kategori:</span>
              <span className="ml-2">{advanceRequest.category}</span>
            </div>
            
            <div className="flex items-center">
              <BuildingIcon className="w-4 h-4 mr-3 text-gray-400" />
              <span className="font-medium">Proje:</span>
              <span className="ml-2">{advanceRequest.project || 'Belirtilmemiş'}</span>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-3 text-gray-400" />
              <span className="font-medium">Talep Tarihi:</span>
              <span className="ml-2">{formatDate(advanceRequest.request_date)}</span>
            </div>
            
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-3 text-gray-400" />
              <span className="font-medium">Kapama Tarihi:</span>
              <span className="ml-2">{formatDate(advanceRequest.closure_date)}</span>
            </div>

            <div className="flex items-start">
              <FileTextIcon className="w-4 h-4 mr-3 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <span className="font-medium">Açıklama:</span>
                <p className="ml-2 text-gray-600 break-words">{advanceRequest.description || 'Açıklama bulunmuyor'}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Expense Lines */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Harcama Detayları ({expenseLines.length} kalem)
        </h3>
        
        {expenseLines.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Henüz harcama kalemi bulunmamaktadır.
          </div>
        ) : (
          <div className="space-y-4">
            {expenseLines.map((line, index) => {
              const DocumentIconComponent = getDocumentIcon(line.attachedFile?.name);
              
              return (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      {/* Row 1: Date, Supplier, Amount */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-6">
                          <div className="flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm font-medium">{formatDate(line.date)}</span>
                          </div>
                          
                          <div className="flex items-center">
                            <BuildingIcon className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm">{line.supplier || 'İşletme belirtilmemiş'}</span>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-lg font-semibold text-purple-600">
                            {formatCurrency(line.amount, line.currency)}
                          </div>
                          {/* Show converted amount if different currency */}
                          {line.currency !== advanceRequest?.currency && convertedAmounts[line.id] && (
                            <div className="text-xs text-gray-500 mt-1">
                              ≈ {formatCurrency(convertedAmounts[line.id], advanceRequest?.currency)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Row 2: Category, Subcategory */}
                      <div className="flex items-center space-x-6">
                        <div className="flex items-center">
                          <FolderIcon className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="text-sm">{line.category}</span>
                          {line.subcategory && (
                            <>
                              <span className="text-gray-400 mx-2">→</span>
                              <span className="text-sm text-gray-600">{line.subcategory}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Row 3: Description */}
                      {line.description && (
                        <div className="flex items-start">
                          <FileTextIcon className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-600">{line.description}</p>
                        </div>
                      )}

                      {/* Row 4: Document Status and Preview */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <Badge 
                            variant={line.documentStatus === 'Belgeli' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {line.documentStatus || 'Belgesiz'}
                          </Badge>
                          
                          {line.attachedFile && (
                            <div className="flex items-center space-x-2">
                              <DocumentIconComponent className="w-4 h-4 text-blue-500" />
                              <span className="text-xs text-gray-600">
                                {line.attachedFile.name}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {line.attachedFile && (
                          <Button
                            onClick={() => handleDocumentPreview(line)}
                            variant="outline"
                            size="sm"
                            className="h-8 px-3 text-xs"
                          >
                            <EyeIcon className="w-3 h-3 mr-1" />
                            Önizle
                          </Button>
                        )}
                      </div>

                      {/* Row 5: Cost Center Info */}
                      {getCostCenterName(line) && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-start space-x-2">
                            <FolderIcon className="w-4 h-4 text-purple-600 mt-0.5" />
                            <div className="flex-1">
                              <span className="text-xs font-medium text-gray-700">Masraf Merkezi: </span>
                              <span className="text-sm text-gray-900 font-medium">
                                {getCostCenterName(line)}
                              </span>
                              <span className="text-xs text-gray-500 ml-2">
                                ({line.cost_center_type === 'project' ? 'Proje' : 'Genel Gider'})
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Row 6: Payment Method Info */}
                      {line.payment_method && line.payment_method !== 'cash' && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-start space-x-2">
                            <BanknoteIcon className="w-4 h-4 text-teal-600 mt-0.5" />
                            <div className="flex-1">
                              <span className="text-xs font-medium text-gray-700">Ödeme Yöntemi: </span>
                              {line.payment_method === 'credit_card' && (line.credit_card_id || line.creditCardId) && (
                                <div className="text-sm text-gray-900 mt-1">
                                  <span className="font-medium">Kredi Kartı</span>
                                  {(() => {
                                    const cardDetails = line.credit_card_details || line.creditCardDetails;
                                    if (cardDetails) {
                                      const bankName = cardDetails?.bank_name || cardDetails?.bankName;
                                      const holderName = cardDetails?.card_holder_name || cardDetails?.cardHolderName;
                                      const last4 = cardDetails?.card_last_4_digits || cardDetails?.cardLast4Digits;
                                      
                                      return (
                                        <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                          {bankName && <div>• Banka: {bankName}</div>}
                                          {holderName && <div>• Kart Sahibi: {holderName}</div>}
                                          {last4 && <div>• Kart: •••• {last4}</div>}
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              )}
                              {line.payment_method === 'bank_transfer' && (line.bank_transfer_details || line.bankTransfer) && (
                                <div className="text-sm text-gray-900 mt-1">
                                  <span className="font-medium">Banka Transferi</span>
                                  {(() => {
                                    const transferDetails = line.bank_transfer_details || line.bankTransfer;
                                    const recipientName = transferDetails?.recipient_name || transferDetails?.recipientName;
                                    const bankName = transferDetails?.bank_name || transferDetails?.bankName;
                                    const description = transferDetails?.transfer_description || transferDetails?.transferDescription;
                                    
                                    return (
                                      <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                        {recipientName && <div>• Alıcı: {recipientName}</div>}
                                        {bankName && <div>• Banka: {bankName}</div>}
                                        {description && <div>• Açıklama: {description}</div>}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Document Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <FileTextIcon className="w-5 h-5" />
              <span>Belge Önizleme</span>
            </DialogTitle>
          </DialogHeader>
          
          {previewDocument && (
            <div className="space-y-4">
              {/* Document Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Dosya Adı:</span>
                    <span className="ml-2">{previewDocument.attachedFile.name}</span>
                  </div>
                  <div>
                    <span className="font-medium">Harcama Kalemi:</span>
                    <span className="ml-2">{previewDocument.supplier} - {formatCurrency(previewDocument.amount, previewDocument.currency)}</span>
                  </div>
                </div>
              </div>

              {/* Preview Area */}
              <div className="border border-gray-200 rounded-lg p-4 bg-white min-h-[400px] flex items-center justify-center">
                {previewDocument.attachedFile.type?.startsWith('image/') ? (
                  <img 
                    src={previewDocument.attachedFile.url}
                    alt="Belge Önizleme"
                    className="max-w-full max-h-[500px] object-contain"
                  />
                ) : (
                  <div className="text-center space-y-4">
                    <FileIcon className="w-16 h-16 text-gray-400 mx-auto" />
                    <div>
                      <p className="text-gray-600">
                        Bu dosya türü için önizleme desteklenmiyor.
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Dosya adı: {previewDocument.attachedFile.name}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowPreviewModal(false)}
                >
                  Kapat
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClosingSummary;